import { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { supabase } from '../lib/supabase';
import { trackEvent, trackMeta, cruiseParams, bookingValue, paidSeats, CURRENCY } from '../lib/analytics';
import { formatCardDate, displayDeck, cruiseLabel, labelBase, borderBase, inputBase } from '../components/shared/helpers';
import ModalShell from '../components/shared/ModalShell';

// Status → muted label shown on non-bookable (disabled) cards.
const STATUS_LABEL = {
  closed: 'Online booking closed',
  sold_out: 'Sold out',
};

function CruiseGroupSection({ group, onSelect }) {
  const isSingle = group.decks.length === 1;

  return (
    <div>

      {/* Mobile heading — one line: cruise name left, sailing time right */}
      <div className="md:hidden flex items-baseline justify-between gap-3 pb-1.5 mb-2 border-b border-[rgba(212,168,67,0.2)]">
        <span className="font-cormorant text-[#C9A84C] text-[1.45rem] leading-[1.15]">
          {cruiseLabel(group)}
        </span>
        <span className="font-dm-sans text-[13.5px] text-white/60 tracking-[0.04em] whitespace-nowrap">
          {group.time}
        </span>
      </div>

      {/* Desktop heading — prominent gold Cormorant, divider on each side */}
      <div className="hidden md:flex items-center gap-5 mb-5">
        <div className="flex-1 h-px bg-[rgba(212,168,67,0.2)]" />
        <div className="flex flex-col items-center shrink-0">
          <span className="font-cormorant text-[#C9A84C] text-3xl leading-tight">
            {cruiseLabel(group)}
          </span>
          <span className="font-dm-sans text-lg text-white/55 tracking-[0.06em] mt-0.5">
            {group.time}
          </span>
        </div>
        <div className="flex-1 h-px bg-[rgba(212,168,67,0.2)]" />
      </div>

      {/* Mobile: decks stacked as thumbnail rows, so every option is on screen
          with no side-scroll · Desktop: equal-height photo cards in a grid */}
      <div
        className={`flex flex-col gap-2 ${
          isSingle
            ? 'md:items-center'
            : 'md:grid md:grid-cols-2 md:gap-6 md:items-stretch'
        }`}
      >
        {group.decks.map((card) => {
          const disabled = !card.bookable;
          return (
          <div
            key={card.key}
            className={`flex gap-3 p-2.5 rounded-xl border md:flex-col md:gap-0 md:p-0 md:h-full md:overflow-hidden ${
              isSingle ? 'md:w-full md:max-w-[500px]' : ''
            } ${disabled ? 'border-white/[0.05] bg-[#0f1c2d]' : 'border-white/[0.09] bg-[#162840]'}`}
          >
            {/* Photo — square thumbnail on mobile, full-width banner on desktop. Muted when disabled. */}
            <div className="relative shrink-0 self-start md:self-auto">
              <img
                src={card.image}
                alt={card.imageAlt}
                className={`w-[88px] h-[88px] object-cover rounded-lg md:w-full md:h-[200px] md:rounded-none ${disabled ? 'grayscale-[0.4]' : ''}`}
              />
              {disabled && (
                <div className="absolute inset-0 bg-[rgba(10,22,40,0.62)] rounded-lg md:rounded-none" aria-hidden="true" />
              )}
            </div>

            {/* Content — price and action share the bottom line on mobile */}
            <div className="flex flex-col flex-1 min-w-0 gap-2 md:gap-0 md:p-4">
              <div>
                <p className={`font-dm-sans text-[15.5px] leading-[22px] md:text-base md:leading-6 font-semibold ${disabled ? 'text-white/55' : 'text-white'}`}>
                  {displayDeck(card.cruiseName, card.deckLabel)}
                </p>
                <p className={`font-dm-sans text-[13px] leading-[18px] md:text-sm md:leading-relaxed font-light mt-0.5 md:mt-1 ${disabled ? 'text-white/55' : 'text-white/60'}`}>
                  {card.desc}
                </p>
              </div>

              {/* Bookable: price beside Select. Not bookable: the status label is too
                  long to sit beside the price at phone width, so the two stack. */}
              <div className={`mt-auto flex md:flex-col md:items-stretch md:gap-4 md:pt-3 ${
                disabled ? 'flex-col items-start gap-1.5' : 'items-center justify-between gap-2.5'
              }`}>
                <p className={`font-dm-sans text-[13px] leading-[18px] md:text-sm md:leading-5 font-medium tabular-nums ${disabled ? 'text-white/55' : 'text-[rgba(212,168,67,0.85)]'}`}>
                  <span className="md:hidden">
                    &#8377;{card.priceAdult.toLocaleString()} adult{disabled ? <> &middot; </> : <br />}&#8377;{card.priceChild.toLocaleString()} child
                  </span>
                  <span className="hidden md:inline">
                    &#8377;{card.priceAdult.toLocaleString()} per adult &middot; &#8377;{card.priceChild.toLocaleString()} per child
                  </span>
                </p>

                {disabled ? (
                  <div
                    className="shrink-0 px-3 py-1 md:w-full md:px-0 md:py-[11px] rounded-full border border-white/[0.12] bg-white/[0.02] text-center select-none"
                    aria-disabled="true"
                  >
                    <span className="font-dm-sans text-[12px] md:text-[13px] font-medium tracking-[0.06em] text-white/55 whitespace-nowrap">
                      {STATUS_LABEL[card.status] || STATUS_LABEL.sold_out}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelect(card)}
                    className="shrink-0 px-5 py-[9px] md:w-full md:px-0 md:py-[11px] rounded-full bg-[#C9A84C] text-[#0A1628] font-dm-sans text-[12.5px] leading-[18px] md:text-[13px] md:leading-normal font-semibold tracking-[0.08em] active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
                  >
                    Select
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

    </div>
  );
}

// Static presentation assets (photos + deck blurb). Prices, times, and
// availability all come from the backend; only the imagery/copy is local.
const DECK_PRESENTATION = {
  'Sunset Cruise|Upper Deck':   { image: '/cruises/sunset-upper-cover.webp',   alt: 'Guests watching the sun set over the Brahmaputra from the open-air upper deck',                desc: 'Open air · Bar & Restaurant' },
  'Sunset Cruise|Middle Deck':  { image: '/cruises/sunset-middle-cover.webp',  alt: 'Covered middle deck with paper lanterns and guests at riverside tables in golden-hour light', desc: 'Covered, open sides · Live Singing & DJ · Bar' },
  'Sundown Cruise|Upper Deck':  { image: '/cruises/sundown-upper-cover.webp',  alt: 'Open-air upper deck lounge lit by planter lights under a starry night sky',                   desc: 'Open air · Bar & Restaurant' },
  'Sundown Cruise|Middle Deck': { image: '/cruises/sundown-middle-cover.webp', alt: 'Middle deck bar and cane seating glowing under paper lanterns at night',                      desc: 'Covered, open sides · Live Singing & DJ · Bar' },
  'Dinner Cruise|Middle Deck':  { image: '/cruises/dinner-cover.webp',         alt: 'Bihu dancers in traditional Assamese attire performing on board',                             desc: 'Cultural & Folk Dance · Live Singing · Buffet' },
};

function presentationFor(cruiseName, deck) {
  return (
    DECK_PRESENTATION[`${cruiseName}|${deck}`] ||
    (deck === 'Upper Deck'
      ? { image: '/cruises/sunset-upper-cover.webp',  alt: 'Guests watching the sun set over the Brahmaputra from the open-air upper deck',                desc: deck }
      : { image: '/cruises/sunset-middle-cover.webp', alt: 'Covered middle deck with paper lanterns and guests at riverside tables in golden-hour light', desc: deck })
  );
}

// "17:00" / "17:00:00" → minutes since midnight, for chronological sorting.
// Unparseable times sort last (Infinity).
function toMinutes(t) {
  if (!t) return Infinity;
  const [hRaw, mRaw] = String(t).split(':');
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw ?? '0', 10);
  if (Number.isNaN(h)) return Infinity;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

// "2026-09-20" → "Sun, 20 Sep" — the compact summary-bar date on mobile.
function shortDate(iso) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  return `${weekday}, ${d} ${month}`;
}

// "17:00" / "17:00:00" → "5:00 PM"
function to12h(t) {
  if (!t) return '';
  const [hRaw, mRaw] = String(t).split(':');
  let h = parseInt(hRaw, 10);
  if (Number.isNaN(h)) return '';
  const m = (mRaw ?? '00').padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

// ─── Private boat enquiry ─────────────────────────────────────────────────────
// Lead capture for the two smaller boats, which are NOT in the booking system:
// no availability, pricing or hold is involved. The card sits after the cruise
// groups (and in the empty state); the form posts to the private-enquiry edge
// function, which writes one online_enquiries row and pings staff. Nothing
// here reads or changes the guest's cruise selection or hold.

// Local calendar date as YYYY-MM-DD, the earliest the date picker offers. The
// server re-checks against IST today, so a timezone edge case is refused
// with a plain message rather than accepted.
function localTodayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function PrivateBoatCard({ submitted, onOpen }) {
  return (
    <div className="rounded-xl border border-[rgba(212,168,67,0.28)] bg-[rgba(201,168,76,0.05)] px-4 py-5 md:px-8 md:py-7 md:max-w-[640px] md:mx-auto md:w-full">
      {submitted ? (
        <>
          <p className="font-cormorant text-[#C9A84C] text-[1.45rem] md:text-3xl leading-[1.15]">
            Thank you, we will call you back
          </p>
          <p className="font-dm-sans text-[13px] md:text-sm leading-relaxed font-light text-white/60 mt-1.5 md:mt-2">
            Your private boat enquiry has been received. Your cruise selection on this page is unchanged.
          </p>
        </>
      ) : (
        <>
          <p className="font-cormorant text-[#C9A84C] text-[1.45rem] md:text-3xl leading-[1.15]">
            Want the boat to yourself?
          </p>
          <p className="font-dm-sans text-[13px] md:text-sm leading-relaxed font-light text-white/60 mt-1.5 md:mt-2">
            We have two smaller boats available for private trips &mdash; birthdays, get-togethers, shoots, or just a quiet ride. Tell us what you have in mind and we&rsquo;ll call you back.
          </p>
          <button
            type="button"
            onClick={onOpen}
            className="mt-4 md:mt-5 px-5 py-[9px] md:py-[11px] rounded-full border border-[rgba(212,168,67,0.55)] text-[#D4A843] font-dm-sans text-[12.5px] md:text-[13px] font-medium tracking-[0.08em] hover:border-[#C9A84C] hover:bg-[rgba(212,168,67,0.08)] active:scale-[0.97] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
          >
            Enquire about a private boat
          </button>
        </>
      )}
    </div>
  );
}

// The form lives in its own component so its values survive a failed submit
// (the modal stays mounted) and are discarded on cancel (it unmounts).
// `initial` carries what the guest already told us: name from the verified
// login, date and party size from the booking context when chosen.
function PrivateBoatModal({ initial, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    phone: '',
    date: initial.date || '',
    people: initial.people ? String(initial.people) : '',
    occasion: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const peopleNum = parseInt(form.people, 10);
  const valid = Boolean(
    form.name.trim() &&
    /^\d{10}$/.test(form.phone) &&
    form.date &&
    Number.isInteger(peopleNum) && peopleNum >= 1 &&
    form.occasion.length <= 500
  );

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('private-enquiry', {
        body: {
          name: form.name.trim(),
          phone: form.phone,
          date: form.date,
          people: peopleNum,
          occasion: form.occasion.trim(),
        },
      });
      // functions.invoke surfaces non-2xx as an error; the JSON body with the
      // server's message is still available on error.context.
      if (fnError) {
        let message = 'Something went wrong. Please try again.';
        try {
          const body = await fnError.context?.json?.();
          if (body?.error) message = body.error;
        } catch (_) {
          /* fall back to the generic message */
        }
        setError(message);
        return;
      }
      if (!data?.ok) {
        setError(data?.error || 'Something went wrong. Please try again.');
        return;
      }
      // The one signal Agam needs: is this path used? PII-free.
      trackEvent('private_enquiry_submitted', { people: peopleNum });
      onSubmitted();
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      ariaLabel="Private boat enquiry"
      onClose={submitting ? undefined : onClose}
    >
      {/* Ornamental diamond rule */}
      <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
        <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
        <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
      </div>

      <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-2">
        Private boat enquiry
      </h2>
      <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-5">
        Tell us a little about your trip and we&rsquo;ll call you back to plan it.
      </p>

      <div className="flex flex-col gap-4">

        {/* Name */}
        <div className="group flex flex-col gap-2">
          <label htmlFor="pb-name" className={labelBase}>Your name</label>
          <div className={borderBase}>
            <input
              id="pb-name"
              type="text"
              value={form.name}
              onChange={set('name')}
              className={inputBase}
              autoComplete="name"
              maxLength={120}
            />
          </div>
        </div>

        {/* Phone */}
        <div className="group flex flex-col gap-2">
          <label htmlFor="pb-phone" className={labelBase}>Phone number</label>
          <div className={`flex items-end gap-3 ${borderBase}`}>
            <span className="font-dm-sans text-[15px] font-light text-[rgba(212,168,67,0.75)] shrink-0 leading-none">+91</span>
            <div className="w-px h-3.5 bg-white/20 self-end mb-0.5 shrink-0" />
            <input
              id="pb-phone"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
              className="flex-1 bg-transparent text-white font-dm-sans text-[15px] font-light outline-none caret-[#D4A843]"
              autoComplete="tel"
            />
          </div>
        </div>

        {/* Date */}
        <div className="group flex flex-col gap-2">
          <label htmlFor="pb-date" className={labelBase}>Preferred date</label>
          <div className={borderBase}>
            <input
              id="pb-date"
              type="date"
              value={form.date}
              min={localTodayIso()}
              onChange={set('date')}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className={`${inputBase} [color-scheme:dark]`}
            />
          </div>
        </div>

        {/* People */}
        <div className="group flex flex-col gap-2">
          <label htmlFor="pb-people" className={labelBase}>Number of people</label>
          <div className={borderBase}>
            <input
              id="pb-people"
              type="text"
              inputMode="numeric"
              value={form.people}
              onChange={(e) => setForm((f) => ({ ...f, people: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
              className={inputBase}
            />
          </div>
        </div>

        {/* Occasion */}
        <div className="group flex flex-col gap-2">
          <label htmlFor="pb-occasion" className={labelBase}>What&rsquo;s the occasion?</label>
          <div className={borderBase}>
            <textarea
              id="pb-occasion"
              rows={3}
              value={form.occasion}
              onChange={set('occasion')}
              maxLength={500}
              className={`${inputBase} resize-none leading-relaxed`}
            />
          </div>
        </div>

      </div>

      <div className="flex flex-col gap-3 mt-6">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!valid || submitting}
          className={`w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
            valid && !submitting
              ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer'
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Sending...' : 'Send enquiry'}
        </button>
        <button
          type="button"
          onClick={() => { if (!submitting) onClose(); }}
          disabled={submitting}
          className="w-full py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12.5px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      {error && (
        <p className="font-dm-sans text-center text-sm text-[#E08A8A] mt-3 leading-relaxed" role="alert">
          {error}
        </p>
      )}
    </ModalShell>
  );
}

export default function Availability() {
  const {
    isVerified, selectedDate, passengers, availabilityCruises,
    customerId, holdId, fullName,
    setSelectedCruise, setPaxDetails, setAgreedTnC,
    setHoldId, setHoldExpiresAt, setAvailabilityCruises,
    holdExpiredNotice, setHoldExpiredNotice,
    fading, go,
  } = useBooking();


  // Hold-confirmation modal (the cruise being confirmed) + transient banner notice.
  const [modalCruise, setModalCruise] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');

  // Private boat enquiry: the form modal, and whether one was sent from this
  // page (the card then reads as a confirmation instead of re-offering).
  const [privateOpen, setPrivateOpen] = useState(false);
  const [privateSubmitted, setPrivateSubmitted] = useState(false);

  // Group the flat availability list into cruise groups shaped for the card layout.
  const groups = useMemo(() => {
    const byCode = new Map();
    for (const c of availabilityCruises || []) {
      if (!byCode.has(c.cruiseCode)) {
        byCode.set(c.cruiseCode, {
          key: c.cruiseCode,
          // cruiseName is the stored name (the key every hold, pricing query and
          // event uses); the display names ride alongside for cruiseLabel() only.
          cruiseName: c.cruiseName,
          displayName: c.displayName ?? null,
          displayNameShort: c.displayNameShort ?? null,
          time: `${to12h(c.departureTime)} – ${to12h(c.endTime)}`,
          departureMinutes: toMinutes(c.departureTime), // shared by all decks; used for ordering
          decks: [],
        });
      }
      const pres = presentationFor(c.cruiseName, c.deck);
      byCode.get(c.cruiseCode).decks.push({
        key: `${c.cruiseCode}-${c.deck}`,
        deckLabel: c.deck,
        image: pres.image,
        imageAlt: pres.alt,
        desc: pres.desc,
        priceAdult: c.adultPrice ?? 0,
        priceChild: c.childPrice ?? 0,
        status: c.status,
        bookable: !!c.bookable,
        cruiseName: c.cruiseName,
        displayName: c.displayName ?? null,
        displayNameShort: c.displayNameShort ?? null,
        cruiseCode: c.cruiseCode,
        departureTime: c.departureTime,
        endTime: c.endTime,
        reportingTime: c.reportingTime,
      });
    }
    // Presentational order: bookable cruises first (a group is bookable if ANY
    // deck is bookable), then non-bookable; within each tier, ascending by the
    // cruise's departure time.
    return Array.from(byCode.values()).sort((a, b) => {
      const aBookable = a.decks.some((d) => d.bookable);
      const bBookable = b.decks.some((d) => d.bookable);
      if (aBookable !== bBookable) return aBookable ? -1 : 1;
      return a.departureMinutes - b.departureMinutes;
    });
  }, [availabilityCruises]);

  // Auto-dismiss the transient banner (sold-out / failed-switch) after a few
  // seconds. Expiry is a blocking modal, not a banner, so it is not handled here.
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  // Re-fetch availability after a fresh sold-out, so the list reflects reality.
  const refreshAvailability = async () => {
    const date = selectedDate;
    try {
      const { data, error } = await supabase.functions.invoke('availability', {
        body: { date, adults: passengers.adults, children: passengers.children, infants: passengers.infants },
      });
      if (!error && data && data.cruises) setAvailabilityCruises(data.cruises);
    } catch (e) {
      // Keep the existing list if the refresh fails.
    }
  };

  // Proceed from the modal → place the hold via the edge function.
  const handleProceed = async () => {
    if (!modalCruise || creating) return;
    const cruise = modalCruise;
    const hadHold = !!holdId;          // distinguishes a switch from a fresh select
    const date = selectedDate;
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('hold', {
        body: {
          action: 'create',
          customerId,
          cruiseType: cruise.cruiseName,
          deck: cruise.deckLabel,
          date,
          adults: passengers.adults,
          children: passengers.children,
          infants: passengers.infants,
        },
      });

      if (error || !data || data.error) {
        setModalCruise(null);
        // The server refuses dates outside the online window (or in the past)
        // with a plain-language message; anything else is a generic failure.
        const dateRefused = data?.error === 'date_out_of_window' || data?.error === 'date_past';
        setNotice(dateRefused && data.message ? data.message : 'Something went wrong while reserving your table. Please try again.');
        return;
      }

      if (data.soldOut) {
        setModalCruise(null);
        if (hadHold) {
          // Switch attempt failed — the existing hold is untouched. Keep everything.
          setNotice('That cruise just sold out — your current hold is still active.');
        } else {
          setNotice('Sorry, that cruise just sold out');
          refreshAvailability();
        }
        return;
      }

      // Held — store the hold + selection, then continue to the cruise summary.
      setSelectedCruise(cruise);
      const rows = [];
      // Passenger 1 (the primary guest) is seeded from the verified account name
      // and is locked in passenger details; the rest start blank.
      for (let i = 0; i < passengers.adults; i++) rows.push({ type: 'adult', label: `Adult ${i + 1}`, name: i === 0 ? (fullName || '') : '', age: '', gender: '', food: '' });
      for (let i = 0; i < passengers.children; i++) rows.push({ type: 'child', label: `Child ${i + 1}`, name: '', age: '', gender: '', food: '' });
      for (let i = 0; i < passengers.infants; i++) rows.push({ type: 'infant', label: `Infant ${i + 1}`, name: '', age: '' });
      setPaxDetails(rows);
      setAgreedTnC(false);
      setHoldId(data.holdId);
      setHoldExpiresAt(data.expiresAt);
      setHoldExpiredNotice(false);
      setNotice('');
      setModalCruise(null);
      // Funnel step 3 — a table is held for this cruise. Value is the public
      // price of the party; `switched` marks a re-selection over an existing hold.
      trackEvent('cruise_selected', {
        ...cruiseParams(cruise, passengers),
        value: bookingValue(cruise, passengers),
        currency: CURRENCY,
        switched: hadHold,
      });
      trackMeta('InitiateCheckout', {
        value: bookingValue(cruise, passengers),
        currency: CURRENCY,
        content_name: cruise?.cruiseName || '',
        num_items: paidSeats(passengers),
      });
      go('/cruise');
    } catch (e) {
      setModalCruise(null);
      setNotice('Something went wrong while reserving your table. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Guard — must be verified
  if (!isVerified) return <Navigate to="/" replace />;

  return (
    <div className={`w-full max-w-[1200px] mx-auto px-4 md:px-6 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Title row — back arrow sits beside the heading to keep the top compact */}
        <div className="flex items-center gap-3 mb-3.5 md:mb-5">
          <button
            type="button"
            onClick={() => go('/passengers')}
            className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="font-cormorant font-normal text-[1.75rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.01em] text-white">
            Available Cruises
          </h1>
        </div>

        {/* Summary bar — one line: date and passengers, each its own edit target.
            Wraps to a second line only when the party string is long. */}
        <div className="border border-[rgba(255,255,255,0.12)] rounded-lg bg-[rgba(255,255,255,0.04)] px-3.5 py-2.5 mb-4 md:mb-10 flex flex-wrap items-center gap-x-3 gap-y-2">

          <button
            type="button"
            onClick={() => go('/select-date')}
            aria-label="Change date"
            className="group flex items-center gap-2 font-dm-sans text-[13.5px] font-light text-white"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className="shrink-0" aria-hidden="true">
              <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M2.5 6.5h11" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M5.5 2v2.5M10.5 2v2.5" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span className="underline decoration-[rgba(201,168,76,0.55)] underline-offset-4 group-hover:decoration-[#C9A84C] transition-colors duration-200">
              <span className="md:hidden">{selectedDate !== null ? shortDate(selectedDate) : ''}</span>
              <span className="hidden md:inline">{formatCardDate(selectedDate)}</span>
            </span>
          </button>

          <span className="w-px h-4 bg-white/[0.12]" aria-hidden="true" />

          <button
            type="button"
            onClick={() => go('/passengers')}
            aria-label="Change passengers"
            className="group flex items-center gap-2 font-dm-sans text-[13.5px] font-light text-white"
          >
            <svg width="16" height="16" viewBox="0 0 18 16" fill="none" className="shrink-0" aria-hidden="true">
              <circle cx="6.5" cy="5" r="2.5" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M2 13.5c0-2.6 2-4.2 4.5-4.2s4.5 1.6 4.5 4.2" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
              <path d="M12.4 4.2a2.3 2.3 0 0 1 0 4.4M13 9.6c1.9.4 3 1.9 3 3.9" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span className="underline decoration-[rgba(201,168,76,0.55)] underline-offset-4 group-hover:decoration-[#C9A84C] transition-colors duration-200">
              {[
                `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
                passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
                passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
              ].filter(Boolean).join(' · ')}
            </span>
          </button>

        </div>

        {/* Transient banner — sold-out or a failed switch (non-blocking) */}
        {notice && (
          <div className="mb-6 rounded-lg border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-4 py-3" role="status">
            <p className="font-dm-sans text-sm font-light text-white/70 leading-relaxed">
              {notice}
            </p>
          </div>
        )}

        {groups.length === 0 ? (
          <div className="flex flex-col gap-4 md:gap-8">
            <p className="font-dm-sans text-sm text-white/55 font-light text-center pt-10 pb-4 leading-relaxed">
              No cruises available for this date
            </p>
            {/* The dead end is the best moment for the private boat offer. */}
            <PrivateBoatCard submitted={privateSubmitted} onOpen={() => setPrivateOpen(true)} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-12">
            {groups.map((group) => (
              <CruiseGroupSection
                key={group.key}
                group={group}
                onSelect={(card) => {
                  setNotice('');
                  // card.cruiseName is the stored name; the display names come with it.
                  setModalCruise({ ...card, time: group.time });
                }}
              />
            ))}
            {/* Last content node: after every cruise group, before the modal
                overlays. Nothing after the groups takes part in the hold flow. */}
            <PrivateBoatCard submitted={privateSubmitted} onOpen={() => setPrivateOpen(true)} />
          </div>
        )}

        {/* Private boat enquiry form. Success closes it and flips the card to
            its confirmation; the cruise selection and any hold are untouched. */}
        {privateOpen && (
          <PrivateBoatModal
            initial={{
              name: fullName,
              date: selectedDate,
              people: (passengers.adults || 0) + (passengers.children || 0) + (passengers.infants || 0),
            }}
            onClose={() => setPrivateOpen(false)}
            onSubmitted={() => { setPrivateOpen(false); setPrivateSubmitted(true); }}
          />
        )}

        {/* Hold-confirmation modal */}
        {modalCruise && (
          <ModalShell
            ariaLabel="Reserve your table"
            onClose={creating ? undefined : () => setModalCruise(null)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-2">
              Reserve your table
            </h2>
            <p className="font-dm-sans text-sm font-medium text-[#C9A84C] mb-3">
              {/* No-break space before the dot: it wraps down with the deck, never ends a line */}
              {cruiseLabel(modalCruise)}&nbsp;&middot; {displayDeck(modalCruise.cruiseName, modalCruise.deckLabel)}
            </p>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Once you proceed, your table is reserved while you complete the booking. If you do not finish in time, the table is released and offered to other guests.
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleProceed}
                disabled={creating}
                className={`w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
                  creating ? 'opacity-60 cursor-not-allowed' : 'active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer'
                }`}
              >
                {creating ? 'Reserving...' : 'Proceed'}
              </button>
              <button
                type="button"
                onClick={() => { if (!creating) setModalCruise(null); }}
                disabled={creating}
                className="w-full py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12.5px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </ModalShell>
        )}

        {/* Hold-expiry notice modal — blocks until acknowledged */}
        {holdExpiredNotice && (
          <ModalShell
            ariaLabel="Booking hold expired"
            onClose={() => setHoldExpiredNotice(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              Your hold has expired
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Your 10-minute hold has ended and the table has been released. Select a cruise again to reserve a table and continue your booking.
            </p>

            <button
              type="button"
              onClick={() => setHoldExpiredNotice(false)}
              className="w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              Choose a cruise
            </button>
          </ModalShell>
        )}

      </div>
  );
}
